/**
 * Cash Flow Store - Pinia store for managing recurring income and expense items
 *
 * This store handles:
 * - Real-time synchronization with Firebase Realtime Database
 * - Automatic calculation of next due dates for recurring items
 * - CRUD operations for income and expense items
 * - State management for cash flow forecasting
 */

import {defineStore} from "pinia";
import {inject, reactive} from "vue";
import {ref as dbRef, set, onValue, Database} from "firebase/database";
import {Frequency, type RecurrentItem} from "@/types.ts";
import dayjs, {Dayjs} from "dayjs";

// Current date reference for calculations
const today : Dayjs = dayjs();

// Flag to track if any item's next due date was modified during processing
let isDirty = false;

/**
 * Calculates the next due date for a recurring item based on its frequency
 * Advances the due date to the next occurrence after today if it's in the past
 *
 * @param item - The recurring item to calculate next due date for
 * @returns Unix timestamp of the next due date
 */
function getNextDue(item : RecurrentItem) : number {
  /**
   * There is no need to calculate the next due date for once only items
   */
  if (item.frequency === Frequency.Once) {
    return item.nextdue;
  }
  let nextdue : Dayjs = dayjs.unix(item.nextdue);
  let delta = 0;
  let unit : "d" | "M" | "y" = "d";

  // Determine the time increment based on frequency
  switch (item.frequency) {
    case Frequency.Weekly :
      delta = 7;
      unit = "d";
      break;

    case Frequency.Fortnightly :
      delta = 14;
      unit = "d";
      break;

    case Frequency.Monthly :
      delta = 1;
      unit = "M";
      break;

    case Frequency.Sixmonthly:
      delta = 6;
      unit = "M";
      break;

    case Frequency.Yearly :
      delta = 1;
      unit = "y";
      break;

    default:
      throw new Error(`Invalid frequency: ${item.frequency}`);
  }

  // Advance the due date until it's after today
  while (!nextdue.isAfter(today)) {
    nextdue = nextdue.add(delta, unit);
    isDirty = true; // Mark that we've modified a date
  }

  return nextdue.unix();
}

/**
 * Interface defining the structure of the cash store state
 */
interface CashStoreState {
  income : RecurrentItem[];   // Array of recurring income items
  expenses : RecurrentItem[]; // Array of recurring expense items
  forecast : [];              // Reserved for future cash flow forecasting
}

/**
 * Filter and process recurring items:
 * - Remove one-time items that are overdue (due before today)
 * - Update next due dates for all remaining items
 *
 * @param items - Array of recurring items to process
 * @returns Filtered and updated array of recurring items
 */
function mapFilter(items : RecurrentItem[]) : RecurrentItem[] {
  return items.filter((item : RecurrentItem) => {
    // Keep recurring items and one-time items that are due today or in the future
    return item.frequency !== Frequency.Once || item.nextdue >= today.unix();
  })
    .map((item : RecurrentItem) => {
      // Update the next due date for each item
      item.nextdue = getNextDue(item);
      return item;
    });
}

// Global flag to track if the store has been initialized with data from Firebase
let isInitialized = false;

/**
 * Main Pinia store for cash flow management
 * Provides reactive state and methods for managing income and expense items
 */
export const useCashStore = defineStore("cashStore", () => {
  // Reactive state containing all cash flow data
  const state : CashStoreState = reactive({
    income   : [],
    expenses : [],
    forecast : [],
  });

  // Inject Firebase database instance from app context
  const db = inject("db") as Database;
  if (!db) {
    throw new Error("Database connection not available.");
  } else {
    console.log("db connected");
  }

  /**
   * Initialize the store by setting up real-time Firebase listeners
   * Returns a promise that resolves when both income and expenses data are loaded
   *
   * @returns Promise that resolves when initialization is complete
   */
  const initializeStore = () : Promise<void> => {
    return new Promise((resolve, reject) => {
      // Create Firebase database references
      const incomeRef = dbRef(db, "income");
      const expenseRef = dbRef(db, "expenses");

      // Track initialization state for both data streams
      let incomeInitialized = false;
      let expensesInitialized = false;
      isInitialized = false;

      console.log("add db listeners");
      // Set up real-time listeners for data changes
      onValue(incomeRef, handleIncomeSnapshot, handleError);
      onValue(expenseRef, handleExpensesSnapshot, handleError);

      /**
       * Handle incoming income data from Firebase
       * Updates local state and triggers rewrite if dates were modified
       */
      function handleIncomeSnapshot(snapshot : any) {
        console.log("Got income snap");
        const newIncome = (snapshot.val() || []) as RecurrentItem[];
        isDirty = false;
        state.income.length = 0; // Clear existing data
        state.income.push(...mapFilter(newIncome)); // Add filtered/updated data
        if (isDirty) {
          console.log("need to rewrite income");
        }
        incomeInitialized = true;
        checkInitialization();
      }

      /**
       * Handle incoming expenses data from Firebase
       * Updates local state and triggers rewrite if dates were modified
       */
      function handleExpensesSnapshot(snapshot : any) {
        const newExpenses = (snapshot.val() || []) as RecurrentItem[];
        console.log("Got expenses snap: ", newExpenses);
        isDirty = false;
        state.expenses.length = 0; // Clear existing data
        state.expenses.push(...mapFilter(newExpenses)); // Add filtered/updated data

        if (isDirty) {
          console.log("need to rewrite expenses");
        }
        console.log("Updated expenses: ", state.expenses);
        expensesInitialized = true;
        checkInitialization();
      }

      /**
       * Handle Firebase errors during initialization or data updates
       */
      function handleError(error : Error) {
//        state.error = error;
        console.error("Firebase error:", error);
        if (!isInitialized) {
          reject(error);
        }
      }

      /**
       * Check if both income and expenses have been loaded
       * Resolve the initialization promise when both are ready
       */
      function checkInitialization() {
        if (incomeInitialized && expensesInitialized && !isInitialized) {
          console.log("set init to true");
          isInitialized = true;
          resolve();
        }
      }
    });
  };

  /**
   * Add a new income item to the store and persist to Firebase
   *
   * @param incomeItem - The recurring income item to add
   * @returns Promise that resolves when the item is saved to Firebase
   */
  const addIncome = (incomeItem : RecurrentItem) : Promise<void> => {
    state.income.push(incomeItem);
    return set(dbRef(db, "income"), state.income);
  };

  /**
   * Add a new expense item to the store and persist to Firebase
   * Filters out Afterpay items to prevent them from being saved to Firebase
   *
   * @param expenseItem - The recurring expense item to add
   * @returns Promise that resolves when the item is saved to Firebase
   */
  const addExpense = (expenseItem : RecurrentItem) : Promise<void> => {
    state.expenses.push(expenseItem);
    // Filter out Afterpay items when saving to Firebase
    const expensesToSave = state.expenses.filter(expense => expense.description !== "Afterpay");
    return set(dbRef(db, "expenses"), expensesToSave);
  };

  /**
   * Update an existing income item by index
   *
   * @param ix - Index of the income item to update
   * @param incomeItem - The updated income item data
   * @returns Promise that resolves when the update is saved to Firebase
   */
  const updateIncome = (ix : number, incomeItem : RecurrentItem) : Promise<void> => {
    state.income.splice(ix, 1, incomeItem);
    return set(dbRef(db, "income"), state.income);
  };

  /**
   * Update an existing expense item by index
   * Filters out Afterpay items to prevent them from being saved to Firebase
   *
   * @param ix - Index of the expense item to update
   * @param expenseItem - The updated expense item data
   * @returns Promise that resolves when the update is saved to Firebase
   */
  const updateExpense = (ix : number, expenseItem : RecurrentItem) : Promise<void> => {
    state.expenses.splice(ix, 1, expenseItem);
    // Filter out Afterpay items when saving to Firebase
    const expensesToSave = state.expenses.filter(expense => expense.description !== "Afterpay");
    return set(dbRef(db, "expenses"), expensesToSave);
  };

  /**
   * Delete an income item by index
   *
   * @param ix - Index of the income item to delete
   * @returns Promise that resolves when the deletion is saved to Firebase
   */
  const deleteIncome = (ix : number) : Promise<void> => {
    state.income.splice(ix, 1);
    return set(dbRef(db, "income"), state.income);
  };

  /**
   * Delete an expense item by index
   * Filters out Afterpay items to prevent them from being saved to Firebase
   *
   * @param ix - Index of the expense item to delete
   * @returns Promise that resolves when the deletion is saved to Firebase
   */
  const deleteExpense = (ix : number) : Promise<void> => {
    state.expenses.splice(ix, 1);
    // Filter out Afterpay items when saving to Firebase
    const expensesToSave = state.expenses.filter(expense => expense.description !== "Afterpay");
    return set(dbRef(db, "expenses"), expensesToSave);
  };

  /**
   * Check if the store has been initialized with data from Firebase
   *
   * @returns True if initialization is complete, false otherwise
   */
  const hasbeenInitialized = () : boolean => {
    return isInitialized;
  };

  /**
   * Write current state to a new Firebase database structure
   * This appears to be for migrating or backing up data
   *
   * @returns Promise that resolves when the data is written to Firebase
   */
  const writeNewDB = () : Promise<void> => {
    const item = {
      income   : state.income,
      expenses : state.expenses,
    };
    return set(dbRef(db, "financials"), item);
  };

  // Return all state properties and methods for use in components
  return {...state, hasbeenInitialized, initializeStore, addIncome, addExpense, updateIncome, updateExpense, deleteExpense, deleteIncome, writeNewDB};
});
